/**
 * @suite/tour — the one-time auto-start hook.
 *
 * Called from a host's authenticated root component: when the surface is
 * enabled, not embedded, and the application's flag has never been written,
 * the tour starts after a short settle delay. Completing or skipping writes
 * the flag, so every later launch stays silent; a bumped `config.version`
 * never restarts the tour by itself.
 *
 * The hook also REGISTERS its config with the provider (replay relay): the
 * separate settings window's replay row reaches the tour through it, since
 * a `?view=settings` document has none of the overlay's targets itself.
 */
import { useEffect, useRef } from "react";
import { useTour } from "./TourContext";
import { isEmbeddedSurface } from "./surface";
import { readTourRecord } from "./tourStorage";
import type { TourConfig, TourLabels } from "./types";

export interface FirstRunTourOptions {
  /** Gate on the host's auth/session state — false while signed out or embedded. */
  enabled?: boolean;
  /** Settle delay before the welcome card appears, in ms. Default 600. */
  delayMs?: number;
  /** Overlay label overrides (Next / Back / Skip / Done / Start / stepOf). */
  labels?: TourLabels;
}

export function useFirstRunTour(config: TourConfig, options?: FirstRunTourOptions): void {
  const { start, registerReplay } = useTour();
  // Refs keep the freshest config/labels without re-arming the timer on every
  // render — a host rebuilding its config with live `t()` strings stays current.
  const configRef = useRef(config);
  configRef.current = config;
  const labelsRef = useRef<TourLabels | undefined>(options?.labels);
  labelsRef.current = options?.labels;

  const enabled = options?.enabled !== false;
  const delayMs = options?.delayMs ?? 600;
  const appId = config.appId;
  const version = config.version;

  useEffect(() => {
    return registerReplay(appId, {
      getConfig: () => configRef.current,
      getLabels: () => labelsRef.current,
    });
  }, [appId, registerReplay]);

  useEffect(() => {
    if (!enabled) return;
    if (isEmbeddedSurface()) return;
    if (readTourRecord(appId)) return;
    const timer = window.setTimeout(() => start(configRef.current, labelsRef.current), delayMs);
    return () => window.clearTimeout(timer);
  }, [enabled, appId, version, delayMs, start]);
}
